import React, { FC, Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Paper, Typography } from '@mui/material';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import ConstructionTotalPrice from './construction-total-price/construction-total-price.component';
import Warranty from 'components/lab-order/warranty/warranty.component';
import ConstructionProperties from 'components/order-flow/lab-order/construction-step/construction-section/construction-properties/construction-properties.component';
import { AvailableConstructionProperties, ConstructionProperty } from 'models/property';
import ActionsAndMaterials from './ations-and-materials/actions-and-materials.component';
import {
    actionPriceLimit,
    perToothQuantity,
    recountPerToothActions,
    syncLinkedMaterialQuantities,
} from './ations-and-materials/actions-and-materials-helper';
import TeethSelection, {
    SelectionMode,
} from 'components/order-flow/shared/constructions/teeth-selection/teeth-selection.component';
import AiAlert from 'components/shared/ai-alert/ai-alert.component';
import RestorationsTable from 'components/scanner-cases/shared/restorations-table/restorations-table.component';
import TandvardCheckbox from 'components/order-flow/action-order/constructions-step/constructions/tandvard-checkbox/tandvard-checkbox.component';
import { ActionGridInput, OrderInputType } from 'models/order-input/shared';
import { LabConstuction } from 'models/order-input/lab-order';

type ConstructionSectionProps = {
    constructionIndex: number;
};
const ConstructionSection: FC<ConstructionSectionProps> = ({ constructionIndex }) => {
    const { t: translate } = useTranslation(['orders', 'scan-cases', 'common']);
    const { order, setOrder, isTioTandvardPricingEnabled } = useContext(LabOrderContext);
    const [originalTioTandvardState] = React.useState(order.constructions[constructionIndex]?.isTioTandvard || false);

    const currentConstruction = order.constructions[constructionIndex];

    const handleWarrantyChanged = (value: boolean, type: string) => {
        setOrder((prev) => {
            const updatedConstructions = prev.constructions.map((construction, idx) =>
                idx === constructionIndex ? { ...construction, warranty: value, warrantyType: type } : construction,
            );
            return { ...prev, constructions: updatedConstructions };
        });
    };

    const handleTandvardCheckbox = (value: boolean) => {
        setOrder((prev) => {
            const updatedConstructions = prev.constructions.map((construction, idx) => {
                if (idx === constructionIndex) {
                    const updateActions = handleTioTandvardActionChange(construction, value);
                    return { ...construction, actions: updateActions, isTioTandvard: value };
                }
                return construction;
            });
            return { ...prev, constructions: updatedConstructions };
        });
    };

    const handleTioTandvardActionChange = (construction: LabConstuction, isTioTandvard: boolean) => {
        if (order.inputType === OrderInputType.NEW) {
            return construction.actions.map((action) => {
                // For tiotandvard orders: prefer customer-specific tiotandvard price, fall back to lab tiotandvard price, then price(resolved in backend with getActionPrice).
                let unitPrice = isTioTandvard
                    ? action.customerSpecificTiotandvardPrice?.toString() ||
                      action.tiotandvardPrice ||
                      action.price?.toString() ||
                      '0'
                    : action.price?.toString() || '0';
                // For simple (non-action-based) tiotandvard orders with no customer-specific tiotandvard price, use the discounted client price if set.
                if (
                    isTioTandvard &&
                    !order.isActionBasedOrder &&
                    !action.customerSpecificTiotandvardPrice &&
                    action.discountedPrice
                ) {
                    unitPrice = action.discountedPrice.toString();
                }
                return {
                    ...action,
                    unitPrice: unitPrice,
                    // On an action set the limit moves with the price: Tio rows are capped at the
                    // TioTandvård price, the same as on the server (DD-3586).
                    ...(order.isActionBasedOrder && {
                        originalPrice: actionPriceLimit(
                            action.basePrice,
                            isTioTandvard,
                            Number(action.tiotandvardPrice) || undefined,
                            action.customerSpecificTiotandvardPrice,
                        ),
                    }),
                } as ActionGridInput;
            });
        }
        return [];
    };

    /**
     * New teeth recount the per-tooth actions, and the materials added for them follow, as the
     * server recounts both on save (DD-3598).
     */
    const withTeeth = (construction: LabConstuction, teethNumbers: number[]): LabConstuction => {
        // Only the clinic's action set has per-tooth actions; the lab's own rows keep their
        // quantity (DD-3766). Same rule as the grid's disableForActionBased.
        const usesClinicActionSet = Boolean(order.customer?._id && order.isActionBasedOrder);
        const actions = recountPerToothActions(
            construction.actions,
            perToothQuantity(teethNumbers),
            usesClinicActionSet,
        );
        return {
            ...construction,
            teethNumbers,
            actions,
            materials: syncLinkedMaterialQuantities(construction.materials, construction.actions, actions),
        };
    };

    const handleTeethSelection = (selectedTeeth?: number[]) => {
        if (selectedTeeth) {
            setOrder((prev) => {
                const updatedConstructions = prev.constructions.map((construction, idx) =>
                    idx === constructionIndex ? withTeeth(construction, selectedTeeth) : construction,
                );
                return { ...prev, constructions: updatedConstructions };
            });
        }
    };

    const handleTeethStateSelection = (selectedTeethState: Map<number, number>) => {
        const teethNumbers = Array.from(selectedTeethState.keys());
        setOrder((prev) => {
            const updatedConstructions = prev.constructions.map((construction, idx) =>
                idx === constructionIndex
                    ? { ...withTeeth(construction, teethNumbers), teethState: selectedTeethState }
                    : construction,
            );
            return { ...prev, constructions: updatedConstructions };
        });
    };

    const handleSettingsChange = (usedProperties: AvailableConstructionProperties, settings: ConstructionProperty) => {
        setOrder((prev) => {
            const updatedConstructions = prev.constructions.map((construction, idx) =>
                idx === constructionIndex ? { ...construction, properties: settings, usedProperties } : construction,
            );
            return { ...prev, constructions: updatedConstructions };
        });
    };

    // An order can reach this step without any part (constructionIndex 0 of an empty list);
    // every child below expects a construction.
    if (!currentConstruction) {
        return null;
    }

    return (
        <Fragment>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Box flexGrow={1}>
                    <Typography variant="h2">
                        {translate('orders:PART')} {constructionIndex + 1}
                    </Typography>
                </Box>
                {(isTioTandvardPricingEnabled || currentConstruction?.isTioTandvard) && (
                    <Box mr={1}>
                        <TandvardCheckbox
                            handleTandvardCheckbox={handleTandvardCheckbox}
                            isTioTandvard={currentConstruction?.isTioTandvard || false}
                        />
                    </Box>
                )}
                <Box mr={1}>
                    <Warranty
                        handleWarranty={handleWarrantyChanged}
                        warranty={currentConstruction?.warranty}
                        warrantyType={currentConstruction?.warrantyType}
                    />
                </Box>
                <Box>
                    <ConstructionTotalPrice
                        construction={currentConstruction}
                        warranty={currentConstruction?.warranty}
                    />
                </Box>
            </Box>
            <Box mb={4}>
                {order.scannerCaseAiAssisted ? (
                    <Box mb={2}>
                        <AiAlert
                            title={translate('common:AI_ASSISTANT')}
                            message={translate('scan-cases:AI_SCANNER_CASE_TEETH_INFO')}
                        />
                    </Box>
                ) : undefined}
                <TeethSelection
                    teeth={currentConstruction?.teethNumbers}
                    teethState={currentConstruction?.teethState}
                    onChange={handleTeethSelection}
                    onTeethStateChange={handleTeethStateSelection}
                    selectionMode={SelectionMode.SINGLE}
                    showJawButtons
                    isPonticExtracted
                />
            </Box>

            {order.scannerCase ? (
                <Box mb={4}>
                    <Box mb={2}>
                        <Typography variant="h3">
                            {translate(`scan-cases:${order.scannerCase?.source}_INFO`)}
                        </Typography>
                    </Box>
                    <Box>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <RestorationsTable restorations={order.scannerCase.restorations} />
                        </Paper>
                    </Box>
                </Box>
            ) : undefined}

            {order.inputType !== OrderInputType.NEW &&
            originalTioTandvardState !== (currentConstruction?.isTioTandvard || false) ? (
                <Box mb={4}>
                    <Alert severity="info">{translate('orders:ORDER_MODIFY_TANDVARD_PRICING_CHANGED_INFO')}</Alert>
                </Box>
            ) : null}
            <ActionsAndMaterials constructionIndex={constructionIndex} />

            <Box>
                <Box mb={2}>
                    <Typography variant="h3">{translate('orders:PROPERTIES')}</Typography>
                </Box>
                <Box>
                    <ConstructionProperties
                        isActionBasedClinic={order.isActionBasedOrder}
                        construction={currentConstruction}
                        onSettingsChange={handleSettingsChange}
                    />
                </Box>
            </Box>
        </Fragment>
    );
};

export default ConstructionSection;
